/* eslint-disable @typescript-eslint/no-unused-vars */
import React from 'react';
import SEO from '../components/SEO';
import {
  Container, Typography, Box, Button, IconButton, Grid, Chip,
  Dialog, DialogContent, TextField, MenuItem, Select, FormControl
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import RemoveShoppingCartIcon from '@mui/icons-material/RemoveShoppingCart';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AddIcon from '@mui/icons-material/Add';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PaymentIcon from '@mui/icons-material/Payment';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import { useNavigate } from 'react-router-dom';

import PageBanner from '../components/PageBanner';
import { useCart } from '../context/CartContext';
import useRazorpay from '../hooks/useRazorpay';

const GOLD = '#d4af37';
const GOLD_LIGHT = '#e5c158';

const ITEM_COLORS = [
  'linear-gradient(135deg, #c0392b, #e74c3c)',
  'linear-gradient(135deg, #2c3e50, #34495e)',
  'linear-gradient(135deg, #1a5276, #2980b9)',
  'linear-gradient(135deg, #d35400, #e67e22)',
  'linear-gradient(135deg, #117a65, #1abc9c)',
  'linear-gradient(135deg, #7d6608, #b8960c)',
];

export default function CartPage() {
  const { cart, removeFromCart, getCartTotal } = useCart();
  const navigate = useNavigate();
  const displayRazorpay = useRazorpay();

  const [billingOpen, setBillingOpen] = React.useState(false);
  const [billing, setBilling] = React.useState({
    name: '', email: '', contact: '', title: '', place: '', state: 'Kerala', pin: '',
  });

  const openBilling = () => setBillingOpen(true);
  const closeBilling = () => setBillingOpen(false);

  const handleBillingChange = (field: string, value: string) => {
    setBilling(prev => ({ ...prev, [field]: value }));
  };

  const handleMakePayment = () => {
    const name = billing.name.trim();
    const email = billing.email.trim();
    const contact = billing.contact.trim();

    if (!name || !email || !contact) {
      alert('Please fill Name, Email and Mobile Number.');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      alert('Please enter a valid email address.');
      return;
    }

    // Validate phone number (Indian 10-digit)
    const phoneRegex = /^[6-9]\d{9}$/;
    if (!phoneRegex.test(contact)) {
      alert('Please enter a valid 10-digit mobile number.');
      return;
    }

    // Validate name length
    if (name.length > 100) {
      alert('Name is too long.');
      return;
    }

    const address = [billing.title, billing.place, billing.state, billing.pin]
      .map(s => s.trim())
      .filter(Boolean)
      .join(', ');

    displayRazorpay({ name, email, contact, address });
    closeBilling();
  };

  const totalAmount = getCartTotal();

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '14px',
      bgcolor: '#fafaf8',
      fontSize: '0.9rem',
      transition: 'all 250ms ease',
      '& fieldset': { borderColor: 'rgba(0,0,0,0.06)' },
      '&:hover fieldset': { borderColor: 'rgba(212,175,55,0.3)' },
      '&.Mui-focused fieldset': { borderColor: GOLD, borderWidth: '1.5px' },
      '&.Mui-focused': { bgcolor: '#fff', boxShadow: '0 0 0 4px rgba(212,175,55,0.06)' },
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.85rem', color: '#bbb',
      '&.Mui-focused': { color: GOLD },
    },
  };

  return (
    <Box sx={{ bgcolor: '#fafaf8', minHeight: '100vh' }}>
      <SEO title="Your Cart" description="Review your pooja and vazhipad bookings at Muchukunnu Sri Kotta-Kovilakam Kshethram before proceeding to payment." path="/cart" keywords="pooja cart, booking cart, checkout" />
      <PageBanner title="Your Cart" />

      <Container maxWidth="lg" sx={{ mt: { xs: -6, md: -8 }, position: 'relative', zIndex: 2, pb: 10 }}>

        {/* ── Decorative floating elements ── */}
        <Box sx={{
          position: 'absolute', top: 60, right: -40, width: 200, height: 200,
          borderRadius: '50%', opacity: 0.03, pointerEvents: 'none',
          background: 'radial-gradient(circle, ' + GOLD + ' 0%, transparent 70%)',
        }} />
        <Box sx={{
          position: 'absolute', top: 300, left: -60, width: 150, height: 150,
          borderRadius: '50%', opacity: 0.03, pointerEvents: 'none',
          background: 'radial-gradient(circle, ' + GOLD + ' 0%, transparent 70%)',
        }} />

        {/* ── Info Bar ── */}
        <Box sx={{
          background: 'linear-gradient(135deg, rgba(26,26,26,0.95) 0%, rgba(45,37,32,0.95) 100%)',
          borderRadius: '20px',
          px: { xs: 2.5, md: 3 }, py: 2,
          mb: 3.5,
          display: 'flex', alignItems: 'center', gap: 2,
          position: 'relative', overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
        }}>
          <Box sx={{
            position: 'absolute', inset: 0, opacity: 0.03,
            backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)',
            backgroundSize: '14px 14px',
          }} />
          <Box sx={{
            width: 40, height: 40, borderRadius: '14px',
            background: 'linear-gradient(135deg, ' + GOLD + ', ' + GOLD_LIGHT + ')',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 16px rgba(212,175,55,0.3)',
            flexShrink: 0,
          }}>
            <VolunteerActivismIcon sx={{ fontSize: 20, color: '#fff' }} />
          </Box>
          <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)', fontWeight: 500, lineHeight: 1.5, position: 'relative', zIndex: 1 }}>
            Dear devotee, if you have not received receipts of your offering, please login and check 'My Account' page.
          </Typography>
        </Box>

        {cart.length === 0 ? (
          /* ── Empty State ── */
          <Box sx={{
            textAlign: 'center', py: { xs: 10, md: 14 }, px: 3,
            bgcolor: '#fff', borderRadius: '32px',
            border: '1px solid rgba(0,0,0,0.04)',
            boxShadow: '0 8px 40px rgba(0,0,0,0.04)',
            position: 'relative', overflow: 'hidden',
          }}>
            {/* Decorative rings */}
            <Box sx={{
              position: 'absolute', top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 300, height: 300, borderRadius: '50%',
              border: '1px solid rgba(212,175,55,0.06)',
              pointerEvents: 'none',
            }} />
            <Box sx={{
              position: 'absolute', top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 400, height: 400, borderRadius: '50%',
              border: '1px solid rgba(212,175,55,0.03)',
              pointerEvents: 'none',
            }} />

            <Box sx={{
              width: 110, height: 110, borderRadius: '50%', mx: 'auto', mb: 3,
              background: 'linear-gradient(135deg, rgba(212,175,55,0.08), rgba(212,175,55,0.02))',
              border: '2px solid rgba(212,175,55,0.08)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              position: 'relative', zIndex: 1,
              animation: 'gentlePulse 3s ease-in-out infinite',
              '@keyframes gentlePulse': {
                '0%, 100%': { transform: 'scale(1)', boxShadow: '0 0 0 0 rgba(212,175,55,0.1)' },
                '50%': { transform: 'scale(1.05)', boxShadow: '0 0 0 20px rgba(212,175,55,0)' },
              },
            }}>
              <RemoveShoppingCartIcon sx={{ fontSize: 48, color: GOLD, opacity: 0.45 }} />
            </Box>
            <Typography sx={{
              fontWeight: 900, fontSize: { xs: '1.5rem', md: '1.8rem' }, color: '#1a1a1a', mb: 1,
              position: 'relative', zIndex: 1,
            }}>
              Your Cart is Empty
            </Typography>
            <Typography sx={{
              color: '#bbb', fontSize: '0.92rem', mb: 1, maxWidth: 380, mx: 'auto',
              position: 'relative', zIndex: 1, lineHeight: 1.6,
            }}>
              Browse our sacred poojas and add your desired offerings to begin your devotion.
            </Typography>

            {/* Gold accent line */}
            <Box sx={{
              width: 60, height: 3, mx: 'auto', my: 3,
              background: 'linear-gradient(90deg, transparent, ' + GOLD + ', transparent)',
              borderRadius: 2,
            }} />

            <Button
              onClick={() => navigate('/poojas/booking')}
              endIcon={<ArrowForwardIcon sx={{ fontSize: '16px !important' }} />}
              sx={{
                bgcolor: GOLD, color: '#fff', fontWeight: 700,
                px: 5, py: 1.5, borderRadius: '18px',
                fontSize: '0.9rem', textTransform: 'none',
                boxShadow: '0 8px 32px rgba(212,175,55,0.35)',
                transition: 'all 300ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                position: 'relative', zIndex: 1,
                '&:hover': {
                  bgcolor: GOLD_LIGHT,
                  transform: 'translateY(-3px) scale(1.02)',
                  boxShadow: '0 14px 40px rgba(212,175,55,0.45)',
                },
              }}
            >
              Browse Poojas
            </Button>
          </Box>
        ) : (
          <Grid container spacing={3.5}>

            {/* ── Left: Cart Items ── */}
            <Grid item xs={12} md={8}>
              {/* Section header */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Box sx={{
                  width: 38, height: 38, borderRadius: '13px',
                  background: 'linear-gradient(135deg, ' + GOLD + ', ' + GOLD_LIGHT + ')',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 4px 16px rgba(212,175,55,0.25)',
                }}>
                  <ShoppingCartIcon sx={{ fontSize: 19, color: '#fff' }} />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.9rem', md: '1.05rem' }, color: '#1a1a1a', lineHeight: 1.2 }}>
                    Your Offerings
                  </Typography>
                  <Typography sx={{ fontSize: '0.68rem', color: '#ccc' }}>
                    {cart.length} item{cart.length > 1 ? 's' : ''} added
                  </Typography>
                </Box>
              </Box>

              {/* Cart item cards */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {cart.map((item, index) => (
                  <Box
                    key={item.cartId}
                    sx={{
                      bgcolor: '#fff', borderRadius: '22px',
                      border: '1px solid rgba(0,0,0,0.04)',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                      overflow: 'hidden',
                      display: 'flex',
                      transition: 'all 350ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 16px 48px rgba(0,0,0,0.08)',
                        '& .itemAccent': { height: '100%' },
                        '& .itemIndex': { transform: 'scale(1.1)' },
                      },
                    }}
                  >
                    {/* Color accent strip */}
                    <Box className="itemAccent" sx={{
                      width: 5, flexShrink: 0,
                      background: ITEM_COLORS[index % ITEM_COLORS.length],
                      transition: 'height 400ms ease',
                      borderRadius: '0 0 0 22px',
                      alignSelf: 'center', height: '60%',
                    }} />

                    <Box sx={{
                      flex: 1, display: 'flex', alignItems: 'flex-start',
                      px: { xs: 2, sm: 2.5 }, py: { xs: 2, sm: 2.5 },
                      gap: { xs: 1.5, sm: 2 },
                    }}>
                      {/* Index orb */}
                      <Box className="itemIndex" sx={{
                        width: { xs: 42, sm: 48 }, height: { xs: 42, sm: 48 },
                        borderRadius: '50%',
                        background: ITEM_COLORS[index % ITEM_COLORS.length],
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff', fontWeight: 900, fontSize: { xs: '0.85rem', sm: '0.95rem' },
                        flexShrink: 0,
                        boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
                        transition: 'transform 350ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                      }}>
                        {index + 1}
                      </Box>

                      {/* Content */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{
                          fontWeight: 700, fontSize: { xs: '0.88rem', sm: '0.95rem' }, color: '#1a1a1a',
                          mb: 0.8, lineHeight: 1.3,
                        }}>
                          {item.name.replace(/\(([^)]+)\)/, '') }
                          {item.name.match(/\(([^)]+)\)/) && (
                            <span className="notranslate" translate="no">{item.name.match(/\(([^)]+)\)/)![0]}</span>
                          )}
                        </Typography>

                        {/* Meta tags */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mb: 1.5 }}>
                          <Box sx={{
                            display: 'inline-flex', alignItems: 'center', gap: 0.5,
                            px: 1.3, py: 0.4, borderRadius: '10px',
                            background: 'linear-gradient(135deg, rgba(212,175,55,0.06), rgba(212,175,55,0.02))',
                            border: '1px solid rgba(212,175,55,0.08)',
                          }}>
                            <PersonOutlineIcon sx={{ fontSize: 13, color: GOLD }} />
                            <Typography sx={{ fontSize: '0.7rem', color: '#888', fontWeight: 600 }}>
                              {item.bookingName}
                            </Typography>
                          </Box>
                          <Box sx={{
                            display: 'inline-flex', alignItems: 'center', gap: 0.5,
                            px: 1.3, py: 0.4, borderRadius: '10px',
                            background: 'linear-gradient(135deg, rgba(212,175,55,0.06), rgba(212,175,55,0.02))',
                            border: '1px solid rgba(212,175,55,0.08)',
                          }}>
                            <StarOutlineIcon sx={{ fontSize: 13, color: GOLD }} />
                            <Typography sx={{ fontSize: '0.7rem', color: '#888', fontWeight: 600 }}>
                              {item.bookingStar}
                            </Typography>
                          </Box>
                          {item.bookingDate && (
                            <Box sx={{
                              display: 'inline-flex', alignItems: 'center', gap: 0.5,
                              px: 1.3, py: 0.4, borderRadius: '10px',
                              background: 'linear-gradient(135deg, rgba(212,175,55,0.06), rgba(212,175,55,0.02))',
                              border: '1px solid rgba(212,175,55,0.08)',
                            }}>
                              <CalendarTodayOutlinedIcon sx={{ fontSize: 12, color: GOLD }} />
                              <Typography sx={{ fontSize: '0.7rem', color: '#888', fontWeight: 600 }}>
                                {new Date(item.bookingDate).toLocaleDateString()}
                              </Typography>
                            </Box>
                          )}
                        </Box>

                        {/* Price row */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{
                            px: 1.5, py: 0.4, borderRadius: '10px',
                            bgcolor: 'rgba(212,175,55,0.06)',
                            border: '1px solid rgba(212,175,55,0.1)',
                          }}>
                            <Typography sx={{ fontSize: '0.7rem', color: GOLD, fontWeight: 700 }}>
                              Qty: {item.quantity}
                            </Typography>
                          </Box>
                          <Typography sx={{
                            fontWeight: 900, fontSize: { xs: '1.1rem', sm: '1.2rem' },
                            color: GOLD, lineHeight: 1,
                          }}>
                            {'\u20B9'}{(item.price * item.quantity).toLocaleString()}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Delete button */}
                      <IconButton
                        onClick={() => removeFromCart(item.cartId)}
                        sx={{
                          width: 40, height: 40, borderRadius: '14px',
                          bgcolor: 'rgba(231,76,60,0.03)',
                          border: '1.5px solid rgba(231,76,60,0.06)',
                          color: '#ddb8b8',
                          flexShrink: 0,
                          transition: 'all 300ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                          '&:hover': {
                            bgcolor: 'rgba(231,76,60,0.08)',
                            borderColor: '#e74c3c',
                            color: '#e74c3c',
                            transform: 'scale(1.12) rotate(-8deg)',
                            boxShadow: '0 6px 20px rgba(231,76,60,0.15)',
                          },
                        }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 19 }} />
                      </IconButton>
                    </Box>
                  </Box>
                ))}
              </Box>

              {/* Add more section */}
              <Box sx={{
                mt: 3, display: 'flex', justifyContent: 'center',
                position: 'relative',
              }}>
                {/* Decorative lines */}
                <Box sx={{
                  position: 'absolute', top: '50%', left: 0, right: 0,
                  height: 1, bgcolor: 'rgba(0,0,0,0.04)',
                }} />
                <Button
                  onClick={() => navigate('/poojas/booking')}
                  startIcon={<AddIcon sx={{ fontSize: '18px !important' }} />}
                  sx={{
                    color: GOLD, fontWeight: 700,
                    fontSize: '0.85rem', textTransform: 'none',
                    px: 4, py: 1.2, borderRadius: '16px',
                    border: '2px solid rgba(212,175,55,0.15)',
                    bgcolor: '#fafaf8',
                    position: 'relative', zIndex: 1,
                    transition: 'all 300ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                    '&:hover': {
                      borderColor: GOLD,
                      bgcolor: 'rgba(212,175,55,0.04)',
                      transform: 'translateY(-3px) scale(1.02)',
                      boxShadow: '0 8px 28px rgba(212,175,55,0.12)',
                    },
                  }}
                >
                  Add More Poojas
                </Button>
              </Box>
            </Grid>

            {/* ── Right: Order Summary ── */}
            <Grid item xs={12} md={4}>
              <Box sx={{
                bgcolor: '#fff', borderRadius: '28px',
                border: '1px solid rgba(0,0,0,0.04)',
                boxShadow: '0 8px 40px rgba(0,0,0,0.04)',
                position: 'sticky', top: 100,
                overflow: 'hidden',
              }}>
                {/* Summary header with rich dark design */}
                <Box sx={{
                  background: 'linear-gradient(145deg, #1a1a1a 0%, #2d2520 50%, #1a1a1a 100%)',
                  px: 3, pt: 3.5, pb: 5,
                  position: 'relative', overflow: 'hidden',
                }}>
                  {/* Dot pattern */}
                  <Box sx={{
                    position: 'absolute', inset: 0, opacity: 0.04,
                    backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)',
                    backgroundSize: '16px 16px',
                  }} />
                  {/* Gold shimmer */}
                  <Box sx={{
                    position: 'absolute', top: 0, left: 0, right: 0, height: 3,
                    background: 'linear-gradient(90deg, transparent, ' + GOLD + ', transparent)',
                    backgroundSize: '200% 100%',
                    animation: 'shimmer 3s infinite linear',
                    '@keyframes shimmer': {
                      '0%': { backgroundPosition: '-200% 0' },
                      '100%': { backgroundPosition: '200% 0' },
                    },
                  }} />
                  {/* Decorative glow */}
                  <Box sx={{
                    position: 'absolute', top: -30, right: -30,
                    width: 100, height: 100, borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(212,175,55,0.15) 0%, transparent 70%)',
                  }} />

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, position: 'relative', zIndex: 1 }}>
                    <Box sx={{
                      width: 38, height: 38, borderRadius: '12px',
                      background: 'linear-gradient(135deg, ' + GOLD + ', ' + GOLD_LIGHT + ')',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: '0 4px 16px rgba(212,175,55,0.3)',
                    }}>
                      <LocalFireDepartmentIcon sx={{ fontSize: 20, color: '#fff' }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.88rem', md: '1rem' }, color: '#fff', lineHeight: 1.2 }}>
                        Order Summary
                      </Typography>
                      <Typography sx={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.3)' }}>
                        {cart.length} pooja{cart.length > 1 ? 's' : ''} in your cart
                      </Typography>
                    </Box>
                  </Box>

                  {/* Curve */}
                  <Box sx={{
                    position: 'absolute', bottom: -1, left: 0, right: 0, height: 24,
                    bgcolor: '#fff',
                    borderRadius: '50% 50% 0 0 / 100% 100% 0 0',
                  }} />
                </Box>

                {/* Items list */}
                <Box sx={{ px: 2.5, pt: 0.5 }}>
                  {cart.map((item, i) => (
                    <Box key={item.cartId} sx={{
                      display: 'flex', alignItems: 'center', gap: 1.5,
                      py: 1.5,
                      borderBottom: i < cart.length - 1 ? '1px solid rgba(0,0,0,0.03)' : 'none',
                    }}>
                      {/* Mini color dot */}
                      <Box sx={{
                        width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                        background: ITEM_COLORS[i % ITEM_COLORS.length],
                        boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                      }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{
                          fontSize: '0.78rem', fontWeight: 600, color: '#444',
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>
                          {item.name.replace(/\(([^)]+)\)/, '') }
                          {item.name.match(/\(([^)]+)\)/) && (
                            <span className="notranslate" translate="no">{item.name.match(/\(([^)]+)\)/)![0]}</span>
                          )}
                        </Typography>
                        <Typography sx={{ fontSize: '0.6rem', color: '#ccc', mt: 0.2 }}>
                          {item.bookingName} \u2022 x{item.quantity}
                        </Typography>
                      </Box>
                      <Typography sx={{
                        fontSize: '0.85rem', fontWeight: 800, color: '#1a1a1a', flexShrink: 0,
                      }}>
                        {'\u20B9'}{(item.price * item.quantity).toLocaleString()}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* Gold divider */}
                <Box sx={{
                  mx: 2.5, my: 2,
                  height: 2, borderRadius: 1,
                  background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.15), transparent)',
                }} />

                {/* Total */}
                <Box sx={{
                  mx: 2.5, mb: 2,
                  px: 2.5, py: 2,
                  background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2520 100%)',
                  borderRadius: '18px',
                  border: '1.5px solid rgba(212,175,55,0.2)',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                }}>
                  <Box>
                    <Typography sx={{
                      fontSize: '0.6rem', fontWeight: 700, color: 'rgba(255,255,255,0.45)',
                      letterSpacing: 1.5, textTransform: 'uppercase', mb: 0.5,
                    }}>
                      Grand Total
                    </Typography>
                    <Typography sx={{
                      fontSize: { xs: '1.3rem', md: '1.7rem' }, fontWeight: 900, lineHeight: 1,
                      letterSpacing: '-0.02em',
                      background: 'linear-gradient(135deg, #f4d03f 0%, #d4af37 50%, #f4d03f 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      textShadow: 'none',
                    }}>
                      {'\u20B9'}{totalAmount.toLocaleString()}
                    </Typography>
                  </Box>
                  <Box sx={{
                    width: 48, height: 48, borderRadius: '50%',
                    background: 'linear-gradient(135deg, rgba(212,175,55,0.15), rgba(212,175,55,0.05))',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <LocalFireDepartmentIcon sx={{ fontSize: 22, color: GOLD, opacity: 0.5 }} />
                  </Box>
                </Box>

                {/* Features */}
                <Box sx={{ mx: 2.5, mb: 2.5 }}>
                  {['Secure Payment', 'Instant Confirmation', 'Digital Receipt'].map((feat, idx) => (
                    <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.6 }}>
                      <CheckCircleOutlineIcon sx={{ fontSize: 14, color: GOLD, opacity: 0.5 }} />
                      <Typography sx={{ fontSize: '0.68rem', color: '#bbb', fontWeight: 500 }}>
                        {feat}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* Payment button */}
                <Box sx={{ px: 2.5, pb: 3 }}>
                  <Button
                    fullWidth
                    onClick={openBilling}
                    endIcon={<ArrowForwardIcon sx={{ fontSize: '16px !important' }} />}
                    sx={{
                      py: 1.8, borderRadius: '18px',
                      background: 'linear-gradient(135deg, ' + GOLD + ' 0%, ' + GOLD_LIGHT + ' 100%)',
                      color: '#fff',
                      fontWeight: 800, fontSize: '0.92rem',
                      textTransform: 'none',
                      boxShadow: '0 10px 36px rgba(212,175,55,0.35)',
                      transition: 'all 300ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                      '&:hover': {
                        transform: 'translateY(-3px) scale(1.01)',
                        boxShadow: '0 16px 48px rgba(212,175,55,0.45)',
                      },
                    }}
                  >
                    Proceed to Payment
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
        )}
      </Container>

      {/* ══════ BILLING DIALOG ══════ */}
      <Dialog
        open={billingOpen}
        onClose={closeBilling}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '28px',
            boxShadow: '0 32px 80px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.04)',
            overflow: 'hidden',
            animation: 'modalSlideIn 450ms cubic-bezier(0.34, 1.56, 0.64, 1)',
            '@keyframes modalSlideIn': {
              '0%': { opacity: 0, transform: 'scale(0.92) translateY(20px)' },
              '100%': { opacity: 1, transform: 'scale(1) translateY(0)' },
            },
          },
        }}
      >
        {/* Dialog header */}
        <Box sx={{
          background: 'linear-gradient(145deg, #1a1a1a 0%, #2d2520 50%, #1a1a1a 100%)',
          px: { xs: 2.5, sm: 3.5 }, pt: 3.5, pb: 5,
          position: 'relative', overflow: 'hidden',
        }}>
          <Box sx={{
            position: 'absolute', inset: 0, opacity: 0.04,
            backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)',
            backgroundSize: '20px 20px',
          }} />
          <Box sx={{
            position: 'absolute', top: 0, left: 0, right: 0, height: 3,
            background: 'linear-gradient(90deg, transparent, ' + GOLD + ', transparent)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 3s infinite linear',
            '@keyframes shimmer': {
              '0%': { backgroundPosition: '-200% 0' },
              '100%': { backgroundPosition: '200% 0' },
            },
          }} />
          {/* Decorative glow */}
          <Box sx={{
            position: 'absolute', top: -20, right: -20,
            width: 120, height: 120, borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(212,175,55,0.12) 0%, transparent 70%)',
          }} />

          <IconButton
            onClick={closeBilling}
            sx={{
              position: 'absolute', top: 14, right: 14, zIndex: 5,
              width: 38, height: 38,
              bgcolor: 'rgba(255,255,255,0.08)',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.1)',
              color: 'rgba(255,255,255,0.6)',
              transition: 'all 300ms cubic-bezier(0.34, 1.56, 0.64, 1)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', transform: 'rotate(90deg)' },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, position: 'relative', zIndex: 2 }}>
            <Box sx={{
              width: 50, height: 50, borderRadius: '16px',
              background: 'linear-gradient(135deg, ' + GOLD + ', ' + GOLD_LIGHT + ')',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 8px 24px rgba(212,175,55,0.35)',
            }}>
              <PaymentIcon sx={{ fontSize: 24, color: '#fff' }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.95rem', md: '1.15rem' }, color: '#fff', mb: 0.3 }}>
                Billing Details
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.35)' }}>
                Enter your billing address to proceed
              </Typography>
            </Box>
          </Box>

          <Box sx={{
            position: 'absolute', bottom: -1, left: 0, right: 0, height: 28,
            bgcolor: '#fff',
            borderRadius: '50% 50% 0 0 / 100% 100% 0 0',
          }} />
        </Box>

        <DialogContent sx={{ px: { xs: 2.5, sm: 3.5 }, pt: 1.5, pb: 2.5 }}>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1 }}>
                <PersonOutlineIcon sx={{ fontSize: 15, color: GOLD }} />
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#555', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                  Full Name *
                </Typography>
              </Box>
              <TextField fullWidth variant="outlined" placeholder="Your name" value={billing.name} onChange={(e) => handleBillingChange('name', e.target.value)} sx={inputSx} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1 }}>
                <EmailOutlinedIcon sx={{ fontSize: 15, color: GOLD }} />
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#555', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                  Email *
                </Typography>
              </Box>
              <TextField fullWidth variant="outlined" placeholder="you@email.com" value={billing.email} onChange={(e) => handleBillingChange('email', e.target.value)} sx={inputSx} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1 }}>
                <PhoneOutlinedIcon sx={{ fontSize: 15, color: GOLD }} />
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#555', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                  Mobile *
                </Typography>
              </Box>
              <TextField fullWidth variant="outlined" placeholder="9876543210" value={billing.contact} onChange={(e) => handleBillingChange('contact', e.target.value)} sx={inputSx} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1 }}>
                <HomeOutlinedIcon sx={{ fontSize: 15, color: GOLD }} />
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#555', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                  Address
                </Typography>
              </Box>
              <TextField fullWidth variant="outlined" placeholder="House/Flat no." value={billing.title} onChange={(e) => handleBillingChange('title', e.target.value)} sx={inputSx} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1 }}>
                <LocationOnOutlinedIcon sx={{ fontSize: 15, color: GOLD }} />
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#555', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                  Place
                </Typography>
              </Box>
              <TextField fullWidth variant="outlined" placeholder="City / Town" value={billing.place} onChange={(e) => handleBillingChange('place', e.target.value)} sx={inputSx} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#555', letterSpacing: 0.5, textTransform: 'uppercase', mb: 1 }}>
                State
              </Typography>
              <FormControl fullWidth sx={inputSx}>
                <Select
                  value={billing.state}
                  onChange={(e) => handleBillingChange('state', e.target.value)}
                  sx={{
                    borderRadius: '14px !important',
                    bgcolor: '#fafaf8', fontSize: '0.9rem',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0,0,0,0.06)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(212,175,55,0.3)' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: GOLD, borderWidth: '1.5px' },
                  }}
                  MenuProps={{ PaperProps: { sx: { borderRadius: '16px', boxShadow: '0 16px 48px rgba(0,0,0,0.12)', mt: 1 } } }}
                >
                  <MenuItem value="Kerala">Kerala</MenuItem>
                  <MenuItem value="Tamil Nadu">Tamil Nadu</MenuItem>
                  <MenuItem value="Karnataka">Karnataka</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#555', letterSpacing: 0.5, textTransform: 'uppercase', mb: 1 }}>
                PIN Code
              </Typography>
              <TextField fullWidth variant="outlined" placeholder="670xxx" value={billing.pin} onChange={(e) => handleBillingChange('pin', e.target.value)} sx={inputSx} />
            </Grid>
          </Grid>
        </DialogContent>

        {/* Dialog footer with amount preview */}
        <Box sx={{
          px: { xs: 2.5, sm: 3.5 }, pb: 3, pt: 2,
          borderTop: '1px solid rgba(0,0,0,0.04)',
        }}>
          {/* Amount summary */}
          <Box sx={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            mb: 2, px: 2, py: 1.5,
            bgcolor: 'rgba(212,175,55,0.04)',
            borderRadius: '14px',
            border: '1px solid rgba(212,175,55,0.08)',
          }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#bbb' }}>
              Amount to pay
            </Typography>
            <Typography sx={{ fontSize: { xs: '0.92rem', md: '1.1rem' }, fontWeight: 900, color: GOLD }}>
              {'\u20B9'}{totalAmount.toLocaleString()}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              fullWidth onClick={closeBilling}
              sx={{
                py: 1.5, borderRadius: '16px',
                fontSize: '0.85rem', fontWeight: 700,
                color: '#999', textTransform: 'none',
                border: '1.5px solid rgba(0,0,0,0.06)',
                transition: 'all 250ms ease',
                '&:hover': { borderColor: 'rgba(0,0,0,0.12)', bgcolor: 'rgba(0,0,0,0.02)', color: '#666' },
              }}
            >
              Cancel
            </Button>
            <Button
              fullWidth onClick={handleMakePayment}
              startIcon={<PaymentIcon sx={{ fontSize: '18px !important' }} />}
              sx={{
                py: 1.5, borderRadius: '16px',
                fontSize: '0.85rem', fontWeight: 700,
                color: '#fff', textTransform: 'none',
                background: 'linear-gradient(135deg, ' + GOLD + ', ' + GOLD_LIGHT + ')',
                boxShadow: '0 8px 28px rgba(212,175,55,0.35)',
                transition: 'all 300ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 36px rgba(212,175,55,0.45)',
                },
              }}
            >
              Make Payment
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
}
